// Upload Storage Utility - Handles file persistence and history management with quota management
const STORAGE_KEYS = {
  CURRENT_UPLOAD_SESSION: 'bulk_upload_current_session',
  UPLOAD_HISTORY: 'bulk_upload_history',
  AUTO_SAVE_PREFIX: 'bulk_upload_autosave_'
};

// Maximum storage size limits (in bytes)
const STORAGE_LIMITS = {
  MAX_SESSION_SIZE: 2 * 1024 * 1024, // 2MB per session
  MAX_TOTAL_SIZE: 4 * 1024 * 1024,   // 4MB total bulk upload data
  MAX_HISTORY_ENTRIES: 50,           // Reduced from 100
  MAX_PRODUCTS_IN_SESSION: 10000     // Limit products per session
};

/**
 * Data compression utilities
 */
const compressionUtils = {
  // Compress data by removing unnecessary fields and truncating large text
  compressSessionData: (data) => {
    if (!data) return null;
    
    const compressed = { ...data };
    
    // Compress products array if present
    if (compressed?.products && Array.isArray(compressed?.products)) {
      compressed.products = compressed?.products?.slice(0, STORAGE_LIMITS?.MAX_PRODUCTS_IN_SESSION)?.map(product => ({
        id: product?.id,
        title: product?.title?.substring(0, 200) || '', // Truncate long titles
        price: product?.price,
        category: product?.category,
        description: product?.description?.substring(0, 300) || '', // Truncate descriptions
        stock: product?.stock,
        condition: product?.condition,
        // Remove heavy fields like images array for session storage
        images: product?.images?.length > 0 ? ['[compressed]'] : []
      }));
    }
    
    // Remove large uploadedFiles data, keep only essential info
    if (compressed?.uploadedFiles && Array.isArray(compressed?.uploadedFiles)) {
      compressed.uploadedFiles = compressed?.uploadedFiles?.map(file => ({
        name: file?.name,
        size: file?.size,
        type: file?.type
      }));
    }
    
    // Remove validation details to save space
    if (compressed?.validationResults?.details) {
      compressed.validationResults = {
        ...compressed?.validationResults,
        details: compressed?.validationResults?.details?.slice(0, 10) // Keep only first 10 issues
      };
    }
    
    return compressed;
  },

  // Get estimated size of data in bytes
  getDataSize: (data) => {
    try {
      return new Blob([JSON.stringify(data)])?.size;
    } catch (error) {
      // Fallback: rough estimation
      return JSON.stringify(data)?.length * 2; // Account for Unicode
    }
  },

  // Chunk large data into smaller pieces
  chunkData: (data, maxChunkSize = 500 * 1024) => { // 500KB chunks
    const serialized = JSON.stringify(data);
    const chunks = [];
    let totalSize = serialized?.length;
    
    if (totalSize <= maxChunkSize) {
      return [data]; // No need to chunk
    }
    
    // For large datasets, split products array into chunks
    if (data?.products && Array.isArray(data?.products)) {
      const productsPerChunk = Math.ceil(maxChunkSize / (totalSize / data?.products?.length));
      for (let i = 0; i < data?.products?.length; i += productsPerChunk) {
        chunks?.push({
          ...data,
          products: data?.products?.slice(i, i + productsPerChunk),
          chunkIndex: Math.floor(i / productsPerChunk),
          totalChunks: Math.ceil(data?.products?.length / productsPerChunk)
        });
      }
    }
    
    return chunks?.length > 0 ? chunks : [data];
  }
};

/**
 * Storage quota management utilities
 */
const quotaManager = {
  // Get current storage usage
  getStorageUsage: () => {
    let totalSize = 0;
    let bulkUploadSize = 0;
    
    try {
      for (const key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          const itemSize = (localStorage.getItem(key) || '')?.length * 2; // Unicode estimation
          totalSize += itemSize;
          
          if (key?.startsWith('bulk_upload_')) {
            bulkUploadSize += itemSize;
          }
        }
      }
    } catch (error) {
      console.warn('Error calculating storage usage:', error);
    }
    
    return {
      totalSize,
      bulkUploadSize,
      totalSizeMB: Math.round(totalSize / (1024 * 1024) * 100) / 100,
      bulkUploadSizeMB: Math.round(bulkUploadSize / (1024 * 1024) * 100) / 100
    };
  },

  // Check if we have enough space for new data
  canStore: (dataSize) => {
    const usage = quotaManager?.getStorageUsage();
    return (usage?.bulkUploadSize + dataSize) <= STORAGE_LIMITS?.MAX_TOTAL_SIZE;
  },

  // Clean up old data to make space
  makeSpace: (requiredSpace = 0) => {
    try {
      let freedSpace = 0;
      const usage = quotaManager?.getStorageUsage();
      
      if (usage?.bulkUploadSize + requiredSpace <= STORAGE_LIMITS?.MAX_TOTAL_SIZE) {
        return true; // Already have enough space
      }
      
      // 1. Clean old history entries
      const history = uploadHistoryManager.getHistory();
      if (history?.length > 10) { // Keep only recent 10 entries
        const recentHistory = history?.slice(0, 10);
        localStorage.setItem(STORAGE_KEYS?.UPLOAD_HISTORY, JSON.stringify(recentHistory));
        freedSpace += (history?.length - 10) * 1000; // Rough estimate
      }
      
      // 2. Clear old auto-save data
      for (const key in localStorage) {
        if (key?.startsWith(STORAGE_KEYS?.AUTO_SAVE_PREFIX)) {
          try {
            const data = JSON.parse(localStorage.getItem(key) || '{}');
            const age = new Date() - new Date(data.timestamp || 0);
            
            // Remove auto-saves older than 1 hour
            if (age > 60 * 60 * 1000) {
              const itemSize = localStorage.getItem(key)?.length * 2;
              localStorage.removeItem(key);
              freedSpace += itemSize;
            }
          } catch (error) {
            // Invalid auto-save data, remove it
            localStorage.removeItem(key);
          }
        }
      }
      
      // 3. If still not enough space, clear current session
      const newUsage = quotaManager?.getStorageUsage();
      if (newUsage?.bulkUploadSize + requiredSpace > STORAGE_LIMITS?.MAX_TOTAL_SIZE) {
        localStorage.removeItem(STORAGE_KEYS?.CURRENT_UPLOAD_SESSION);
        console.warn('Cleared current session to make space for new data');
      }
      
      return true;
    } catch (error) {
      console.error('Error making space:', error);
      return false;
    }
  }
};

/**
 * Upload session management for file persistence
 */
export const uploadSessionManager = {
  // Save current upload session with quota management
  saveSession: (sessionData) => {
    try {
      // Compress data first
      const compressedData = compressionUtils?.compressSessionData({
        ...sessionData,
        timestamp: new Date()?.toISOString(),
        sessionId: Date.now()?.toString()
      });
      
      if (!compressedData) {
        console.warn('No data to save in session');
        return false;
      }
      
      let serializedData = JSON.stringify(compressedData);
      const dataSize = compressionUtils?.getDataSize(compressedData);
      
      // Check if data size is reasonable
      if (dataSize > STORAGE_LIMITS?.MAX_SESSION_SIZE) {
        console.warn('Session data too large, further compression needed');
        
        // Further compress by keeping only essential data
        const essentialData = {
          currentStep: compressedData?.currentStep,
          products: compressedData?.products?.slice(0, 100), // Keep only first 100 products
          timestamp: compressedData?.timestamp,
          sessionId: compressedData?.sessionId,
          productCount: compressedData?.products?.length || 0
        };
        
        const essentialSerialized = JSON.stringify(essentialData);
        const essentialSize = compressionUtils?.getDataSize(essentialData);
        
        if (essentialSize > STORAGE_LIMITS?.MAX_SESSION_SIZE) {
          console.error('Even essential data is too large for storage');
          return false;
        }
        
        // Use essential data instead
        serializedData = essentialSerialized;
      }
      
      // Make space if needed
      if (!quotaManager?.canStore(serializedData?.length * 2)) {
        if (!quotaManager?.makeSpace(serializedData?.length * 2)) {
          console.error('Cannot make enough space for session data');
          return false;
        }
      }
      
      localStorage.setItem(STORAGE_KEYS?.CURRENT_UPLOAD_SESSION, serializedData);
      console.log('Upload session saved successfully');
      return true;
      
    } catch (error) {
      console.error('Error saving upload session:', error);
      
      if (error?.name === 'QuotaExceededError') {
        // Try to make space and retry once
        if (quotaManager?.makeSpace()) {
          try {
            // Retry with minimal data
            const minimalData = {
              currentStep: sessionData?.currentStep || 'upload',
              productCount: sessionData?.products?.length || 0,
              timestamp: new Date()?.toISOString(),
              sessionId: Date.now()?.toString()
            };
            
            localStorage.setItem(STORAGE_KEYS?.CURRENT_UPLOAD_SESSION, JSON.stringify(minimalData));
            console.warn('Saved minimal session data due to quota exceeded');
            return true;
          } catch (retryError) {
            console.error('Failed to save even minimal session data:', retryError);
          }
        }
      }
      
      return false;
    }
  },

  // Load current upload session
  loadSession: () => {
    try {
      const sessionData = localStorage.getItem(STORAGE_KEYS?.CURRENT_UPLOAD_SESSION);
      if (sessionData) {
        const parsed = JSON.parse(sessionData);
        // Check if session is not older than 24 hours
        const sessionAge = new Date() - new Date(parsed.timestamp);
        if (sessionAge < 24 * 60 * 60 * 1000) {
          return parsed;
        } else {
          // Remove expired session
          uploadSessionManager?.clearSession();
        }
      }
      return null;
    } catch (error) {
      console.error('Error loading upload session:', error);
      // Clean up corrupted session data
      uploadSessionManager?.clearSession();
      return null;
    }
  },

  // Clear current session
  clearSession: () => {
    try {
      localStorage.removeItem(STORAGE_KEYS?.CURRENT_UPLOAD_SESSION);
      return true;
    } catch (error) {
      console.error('Error clearing upload session:', error);
      return false;
    }
  },

  // Auto-save functionality with compression
  autoSave: (stepData, stepName) => {
    try {
      const key = `${STORAGE_KEYS?.AUTO_SAVE_PREFIX}${stepName}`;
      
      // Compress the step data
      const compressedStepData = compressionUtils?.compressSessionData({
        ...stepData,
        timestamp: new Date()?.toISOString(),
        step: stepName
      });
      
      const dataSize = compressionUtils?.getDataSize(compressedStepData);
      
      // Skip auto-save if data is too large
      if (dataSize > STORAGE_LIMITS?.MAX_SESSION_SIZE / 2) {
        console.warn(`Auto-save skipped for ${stepName}: data too large`);
        return false;
      }
      
      // Make space if needed
      if (!quotaManager?.canStore(dataSize)) {
        quotaManager?.makeSpace(dataSize);
      }
      
      localStorage.setItem(key, JSON.stringify(compressedStepData));
      return true;
    } catch (error) {
      console.error('Error auto-saving:', error);
      
      if (error?.name === 'QuotaExceededError') {
        // Skip auto-save if quota exceeded
        console.warn(`Auto-save skipped for ${stepName}: quota exceeded`);
      }
      
      return false;
    }
  },

  // Load auto-saved data
  loadAutoSaved: (stepName) => {
    try {
      const key = `${STORAGE_KEYS?.AUTO_SAVE_PREFIX}${stepName}`;
      const data = localStorage.getItem(key);
      if (data) {
        return JSON.parse(data);
      }
      return null;
    } catch (error) {
      console.error('Error loading auto-saved data:', error);
      return null;
    }
  },

  // Clear auto-saved data for specific step
  clearAutoSaved: (stepName) => {
    try {
      const key = `${STORAGE_KEYS?.AUTO_SAVE_PREFIX}${stepName}`;
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Error clearing auto-saved data:', error);
      return false;
    }
  }
};

/**
 * Upload history management with quota limits
 */
export const uploadHistoryManager = {
  // Add new upload to history with size management
  addToHistory: (uploadData) => {
    try {
      const history = uploadHistoryManager?.getHistory();
      const rememberedEmail = () => {
        try {
          return localStorage?.getItem('mlbp:lastUserEmail') || null;
        } catch (err) {
          console.warn('Unable to read last user email from storage:', err?.message);
          return null;
        }
      };

      const normalizedEntry = {
        ...uploadData,
        id: Date.now(),
        date: new Date()?.toISOString(),
        addedAt: new Date()?.toISOString()
      };

      if (!normalizedEntry?.uploadedBy) {
        normalizedEntry.uploadedBy = rememberedEmail() || 'Usuario desconocido';
      }

      const newEntry = normalizedEntry;
      
      history?.unshift(newEntry); // Add to beginning of array
      
      // Keep only allowed number of entries
      if (history?.length > STORAGE_LIMITS?.MAX_HISTORY_ENTRIES) {
        history?.splice(STORAGE_LIMITS?.MAX_HISTORY_ENTRIES);
      }
      
      const historySize = compressionUtils?.getDataSize(history);
      
      // Make space if needed
      if (!quotaManager?.canStore(historySize)) {
        // Reduce history size by removing oldest entries
        const reducedHistory = history?.slice(0, Math.floor(STORAGE_LIMITS?.MAX_HISTORY_ENTRIES / 2));
        localStorage.setItem(STORAGE_KEYS?.UPLOAD_HISTORY, JSON.stringify(reducedHistory));
        console.log('History trimmed due to storage constraints');
      } else {
        localStorage.setItem(STORAGE_KEYS?.UPLOAD_HISTORY, JSON.stringify(history));
      }
      
      console.log('Upload added to history successfully');
      return newEntry;
    } catch (error) {
      console.error('Error adding to upload history:', error);
      
      if (error?.name === 'QuotaExceededError') {
        // Try with minimal data
        try {
          const minimalEntry = {
            id: Date.now(),
            fileName: uploadData?.fileName || 'Unknown',
            publicationRunId: uploadData?.publicationRunId || null,
            totalProducts: uploadData?.totalProducts || 0,
            status: uploadData?.status || 'completed',
            date: new Date()?.toISOString()
          };
          
          const history = uploadHistoryManager?.getHistory();
          history?.unshift(minimalEntry);
          history?.splice(10); // Keep only 10 entries
          
          localStorage.setItem(STORAGE_KEYS?.UPLOAD_HISTORY, JSON.stringify(history));
          return minimalEntry;
        } catch (retryError) {
          console.error('Failed to save minimal history entry:', retryError);
        }
      }
      
      return null;
    }
  },

  // Get upload history
  getHistory: () => {
    try {
      const history = localStorage.getItem(STORAGE_KEYS?.UPLOAD_HISTORY);
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error('Error getting upload history:', error);
      // Clean up corrupted history
      localStorage.setItem(STORAGE_KEYS?.UPLOAD_HISTORY, JSON.stringify([]));
      return [];
    }
  },

  // Update existing history entry
  updateHistoryEntry: (entryId, updates) => {
    try {
      const history = uploadHistoryManager?.getHistory();
      const entryIndex = history?.findIndex(entry => entry?.id === entryId);
      
      if (entryIndex !== -1) {
        history[entryIndex] = {
          ...history?.[entryIndex],
          ...updates,
          lastUpdated: new Date()?.toISOString()
        };
        
        localStorage.setItem(STORAGE_KEYS?.UPLOAD_HISTORY, JSON.stringify(history));
        return history?.[entryIndex];
      }
      return null;
    } catch (error) {
      console.error('Error updating history entry:', error);
      return null;
    }
  },

  // Remove entry from history
  removeFromHistory: (entryId) => {
    try {
      const history = uploadHistoryManager?.getHistory();
      const filteredHistory = history?.filter(entry => entry?.id !== entryId);
      localStorage.setItem(STORAGE_KEYS?.UPLOAD_HISTORY, JSON.stringify(filteredHistory));
      return true;
    } catch (error) {
      console.error('Error removing from history:', error);
      return false;
    }
  },

  // Clear all history
  clearHistory: () => {
    try {
      localStorage.setItem(STORAGE_KEYS?.UPLOAD_HISTORY, JSON.stringify([]));
      return true;
    } catch (error) {
      console.error('Error clearing history:', error);
      return false;
    }
  }
};

/**
 * Full upload-session persistence.
 * IndexedDB avoids the small localStorage quota and preserves complete products.
 */
const FULL_SESSION_DB_NAME = 'mercadobulk_bulk_upload';
const FULL_SESSION_STORE = 'sessions';
const FULL_SESSION_KEY = 'current';

const fullSessionStorage = {
  open: () => new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }

    const request = indexedDB.open(FULL_SESSION_DB_NAME, 1);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(FULL_SESSION_STORE)) {
        db.createObjectStore(FULL_SESSION_STORE, { keyPath: 'key' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  }),

  save: async (session) => {
    try {
      const db = await fullSessionStorage.open();
      if (!db) return false;

      return await new Promise((resolve) => {
        const tx = db.transaction(FULL_SESSION_STORE, 'readwrite');

        tx.objectStore(FULL_SESSION_STORE).put({
          key: FULL_SESSION_KEY,
          value: session,
          updatedAt: new Date().toISOString()
        });

        tx.oncomplete = () => {
          db.close();
          resolve(true);
        };

        tx.onerror = () => {
          console.error('Error guardando sesión completa:', tx.error);
          db.close();
          resolve(false);
        };

        tx.onabort = () => {
          db.close();
          resolve(false);
        };
      });
    } catch (error) {
      console.error('Error guardando sesión completa:', error);
      return false;
    }
  },

  load: async () => {
    try {
      const db = await fullSessionStorage.open();
      if (!db) return null;

      return await new Promise((resolve) => {
        const tx = db.transaction(FULL_SESSION_STORE, 'readonly');
        const request = tx.objectStore(FULL_SESSION_STORE).get(FULL_SESSION_KEY);

        request.onsuccess = () => {
          const value = request.result?.value || null;
          db.close();
          resolve(value);
        };

        request.onerror = () => {
          db.close();
          resolve(null);
        };
      });
    } catch (error) {
      console.error('Error recuperando sesión completa:', error);
      return null;
    }
  },

  clear: async () => {
    try {
      const db = await fullSessionStorage.open();
      if (!db) return false;

      return await new Promise((resolve) => {
        const tx = db.transaction(FULL_SESSION_STORE, 'readwrite');
        tx.objectStore(FULL_SESSION_STORE).delete(FULL_SESSION_KEY);

        tx.oncomplete = () => {
          db.close();
          resolve(true);
        };

        tx.onerror = () => {
          db.close();
          resolve(false);
        };
      });
    } catch (error) {
      console.error('Error eliminando sesión completa:', error);
      return false;
    }
  }
};

/**
 * File recovery utilities
 */
export const fileRecoveryManager = {
  saveFullSession: (session) => fullSessionStorage.save(session),

  loadFullSession: () => fullSessionStorage.load(),

  // Check legacy localStorage recovery data
  hasRecoverableData: () => {
    const session = uploadSessionManager?.loadSession();
    const autoSavedUpload = uploadSessionManager?.loadAutoSaved('upload');
    const autoSavedPreview = uploadSessionManager?.loadAutoSaved('preview');

    return !!(session || autoSavedUpload || autoSavedPreview);
  },

  // Legacy fallback
  getRecoveryData: () => {
    return {
      session: uploadSessionManager?.loadSession(),
      autoSavedUpload: uploadSessionManager?.loadAutoSaved('upload'),
      autoSavedPreview: uploadSessionManager?.loadAutoSaved('preview')
    };
  },

  // Clear ALL recovery data only when user starts over
  // or the publication process finishes successfully.
  clearRecoveryData: () => {
    uploadSessionManager?.clearSession();
    uploadSessionManager?.clearAutoSaved('upload');
    uploadSessionManager?.clearAutoSaved('preview');
    uploadSessionManager?.clearAutoSaved('publish');
    fullSessionStorage.clear();

    try {
      if (typeof window !== 'undefined') {
        delete window.__MERCADOBULK_UPLOAD_RUNTIME__;
      }
    } catch {}
  }
};

/**
 * Enhanced storage space management
 */
export const storageManager = {
  // Get comprehensive storage usage info
  getStorageInfo: () => {
    const usage = quotaManager?.getStorageUsage();
    const storageInfo = {};
    
    try {
      for (const key in localStorage) {
        if (localStorage.hasOwnProperty(key) && key?.startsWith('bulk_upload_')) {
          const size = (localStorage.getItem(key) || '')?.length * 2;
          storageInfo[key] = {
            size,
            sizeKB: Math.round(size / 1024 * 100) / 100,
            sizeMB: Math.round(size / (1024 * 1024) * 100) / 100
          };
        }
      }
    } catch (error) {
      console.error('Error getting storage details:', error);
    }
    
    return {
      ...usage,
      details: storageInfo,
      limits: STORAGE_LIMITS,
      isNearLimit: usage?.bulkUploadSize > (STORAGE_LIMITS?.MAX_TOTAL_SIZE * 0.8),
      availableSpace: Math.max(0, STORAGE_LIMITS?.MAX_TOTAL_SIZE - usage?.bulkUploadSize)
    };
  },

  // Check if storage is near limit
  isStorageNearLimit: () => {
    const usage = quotaManager?.getStorageUsage();
    return usage?.bulkUploadSize > (STORAGE_LIMITS?.MAX_TOTAL_SIZE * 0.8);
  },

  // Clean up old data with improved logic
  cleanupOldData: () => {
    try {
      let cleanedItems = 0;
      
      // 1. Clean old history entries
      const history = uploadHistoryManager.getHistory();
      const cutoffDate = new Date();
      cutoffDate?.setMonth(cutoffDate?.getMonth() - 1); // Keep 1 month instead of 3
      
      const recentHistory = history?.filter(entry => 
        new Date(entry.date) > cutoffDate
      )?.slice(0, STORAGE_LIMITS?.MAX_HISTORY_ENTRIES);
      
      if (recentHistory?.length !== history?.length) {
        localStorage.setItem(STORAGE_KEYS?.UPLOAD_HISTORY, JSON.stringify(recentHistory));
        cleanedItems += history?.length - recentHistory?.length;
      }
      
      // 2. Clean old auto-save data
      for (const key in localStorage) {
        if (key?.startsWith(STORAGE_KEYS?.AUTO_SAVE_PREFIX)) {
          try {
            const data = JSON.parse(localStorage.getItem(key) || '{}');
            const age = new Date() - new Date(data.timestamp || 0);
            
            // Remove auto-saves older than 2 hours
            if (age > 2 * 60 * 60 * 1000) {
              localStorage.removeItem(key);
              cleanedItems++;
            }
          } catch (error) {
            // Invalid auto-save data, remove it
            localStorage.removeItem(key);
            cleanedItems++;
          }
        }
      }
      
      return cleanedItems;
    } catch (error) {
      console.error('Error cleaning up old data:', error);
      return 0;
    }
  },

  // Force cleanup when quota exceeded
  forceCleanup: () => {
    try {
      // Clear all auto-saves
      for (const key in localStorage) {
        if (key?.startsWith(STORAGE_KEYS?.AUTO_SAVE_PREFIX)) {
          localStorage.removeItem(key);
        }
      }
      
      // Keep only 5 most recent history entries
      const history = uploadHistoryManager.getHistory();
      const minimalHistory = history?.slice(0, 5);
      localStorage.setItem(STORAGE_KEYS?.UPLOAD_HISTORY, JSON.stringify(minimalHistory));
      
      // Clear current session
      uploadSessionManager?.clearSession();
      
      console.log('Force cleanup completed');
      return true;
    } catch (error) {
      console.error('Error during force cleanup:', error);
      return false;
    }
  }
};

export default {
  uploadSessionManager,
  uploadHistoryManager,
  fileRecoveryManager,
  storageManager,
  quotaManager,
  compressionUtils
};